export type CaseStatus = 'open' | 'investigating' | 'pending_review' | 'closed';
export type CasePriority = 'low' | 'medium' | 'high' | 'critical';
export type TicketStatus = 'open' | 'in_progress' | 'escalated' | 'resolved' | 'closed';

export type IocType =
  | 'IP'
  | 'DOMAIN'
  | 'URL'
  | 'EMAIL'
  | 'HASH_MD5'
  | 'HASH_SHA1'
  | 'HASH_SHA256'
  | 'BTC_ADDRESS'
  | 'ETH_ADDRESS'
  | 'MSISDN';

export type AuditEventType =
  | 'login'
  | 'logout'
  | 'session_refresh'
  | 'delete_message'
  | 'edit_message'
  | 'export_chat'
  | 'export_forensic'
  | 'export_contacts'
  | 'export_session_strings'
  | 'import_session_strings'
  | 'download_file'
  | 'upload_evidence'
  | 'download_evidence'
  | 'screenshot_attempt'
  | 'ip_lookup'
  | 'case_created'
  | 'case_updated'
  | 'ticket_created'
  | 'anomaly_login';

export interface TimelineEntry {
  id: string;
  at: number;
  kind: 'audit' | 'case' | 'evidence' | 'ioc' | 'message' | 'system';
  title: string;
  detail?: string;
  caseId?: string;
  meta?: Record<string, string | number | boolean | undefined>;
}

export interface CaseChecklistItem {
  id: string;
  label: string;
  done: boolean;
}

export interface CaseRoom {
  id: string;
  title: string;
  description?: string;
  status: CaseStatus;
  priority: CasePriority;
  assignedInvestigators: string[];
  linkedChatIds: string[];
  evidenceTags: string[];
  checklist: CaseChecklistItem[];
  timeline: TimelineEntry[];
  createdAt: number;
  updatedAt: number;
}

export interface CustodyEvent {
  at: number;
  action: 'uploaded' | 'downloaded' | 'viewed' | 'exported' | 'hash_verified';
  userId?: string;
  userLabel?: string;
  note?: string;
}

export interface EvidenceRecord {
  id: string;
  caseId?: string;
  filename: string;
  mimeType: string;
  size: number;
  sha256: string;
  uploadedBy?: string;
  uploadedAt: number;
  tags: string[];
  custody: CustodyEvent[];
  malwareScan?: {
    entropy?: number;
    fileTypeGuess?: string;
    stringCount?: number;
    peSignature?: boolean;
    scannedAt: number;
  };
}

export interface IocIndexEntry {
  type: IocType;
  value: string;
  normalized: string;
  chatId?: string;
  messageId?: number;
  caseId?: string;
  seenAt: number;
}

export interface IntelNote {
  id: string;
  caseId?: string;
  title: string;
  markdown: string;
  relatedNoteIds: string[];
  updatedAt: number;
  createdAt: number;
}

export interface InternalTicket {
  id: string;
  caseId?: string;
  title: string;
  description?: string;
  status: TicketStatus;
  assignee?: string;
  dueAt?: number;
  priority: CasePriority;
  createdAt: number;
  updatedAt: number;
}

export interface AuditEvent {
  id: string;
  type: AuditEventType;
  at: number;
  userId?: string;
  ip?: string;
  deviceFingerprint?: string;
  userAgent?: string;
  chatId?: string;
  messageId?: number;
  detail?: string;
  meta?: Record<string, string | number | boolean | undefined>;
}

export interface DeviceSessionRecord {
  fingerprint: string;
  userId?: string;
  userAgent: string;
  platform: string;
  firstSeen: number;
  lastSeen: number;
  label?: string;
  trusted: boolean;
}

export interface LocalNotificationRecord {
  id: string;
  at: number;
  title: string;
  body: string;
  read: boolean;
  level: 'info' | 'warning' | 'alert';
}

export interface IpLookupResult {
  ip: string;
  hostname?: string;
  country_name?: string;
  country_code?: string;
  region_name?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  timezone?: { id?: string; current_time?: string };
  connection?: { isp?: string; asn?: number };
  security?: {
    is_proxy?: boolean;
    is_tor?: boolean;
    threat_level?: string;
  };
  location?: { country_flag_emoji?: string };
}

export interface InvestigationSettings {
  iocBadgesEnabled: boolean;
  browserNotifications: boolean;
  soundAlerts: boolean;
}

export interface InvestigationDb {
  version: 1;
  settings: InvestigationSettings;
  cases: Record<string, CaseRoom>;
  auditEvents: AuditEvent[];
  evidence: Record<string, EvidenceRecord>;
  iocIndex: Record<string, IocIndexEntry[]>;
  notes: Record<string, IntelNote>;
  tickets: Record<string, InternalTicket>;
  deviceSessions: DeviceSessionRecord[];
  notifications: LocalNotificationRecord[];
  ipCache: Record<string, { data: IpLookupResult; cachedAt: number }>;
}

export function createEmptyInvestigationDb(): InvestigationDb {
  return {
    version: 1,
    settings: {
      iocBadgesEnabled: true,
      browserNotifications: true,
      soundAlerts: false,
    },
    cases: {},
    auditEvents: [],
    evidence: {},
    iocIndex: {},
    notes: {},
    tickets: {},
    deviceSessions: [],
    notifications: [],
    ipCache: {},
  };
}
